/**
 * Custom hook for fetching user profiles from Firestore
 * UPDATED: Added search functionality
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  startAfter,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Custom hook to fetch user profiles from Firestore
 * 
 * @param {Object} options - Options for fetching profiles
 * @param {string} options.university - Filter by university
 * @param {string} options.course - Filter by course
 * @param {Array} options.interests - Filter by interests
 * @param {Array} options.personalities - Filter by personalities
 * @param {number} options.limitCount - Number of profiles to fetch
 * @param {boolean} options.onlyOnline - Filter by online status
 * @param {string} options.searchQuery - Search query for name/username/university
 * @returns {Object} Profiles data and loading state
 */
const useProfiles = (options = {}) => {
  const [profiles, setProfiles] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]); // Store all fetched profiles for search
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const {
    university = null,
    course = null,
    interests = [],
    personalities = [],
    limitCount = 20, // Increased default for better search results
    onlyOnline = false,
    searchQuery = ''
  } = options;

  // Normalize search query
  const normalizedSearchQuery = searchQuery.toLowerCase().trim();

  // Search function to filter profiles
  const searchProfiles = (profilesToSearch, query) => {
    if (!query) return profilesToSearch;
    
    const searchTerms = query.split(' ').filter(term => term.length > 0);
    
    return profilesToSearch.filter(profile => {
      const searchableText = [
        profile.basicInfo?.name || '',
        profile.basicInfo?.username || '',
        profile.academic?.university || '',
        profile.academic?.degree || '',
        profile.bio?.shortBio || '',
        ...(profile.bio?.interests || []),
        ...(profile.bio?.personalities || [])
      ].join(' ').toLowerCase();
      
      // Check if all search terms are found in the searchable text
      return searchTerms.every(term => searchableText.includes(term));
    });
  };

  // Fetch profiles based on filters (without search)
  const fetchProfiles = async (isLoadMore = false) => {
    try {
      setLoading(true);
      
      // Start building the query
      let profilesQuery = collection(db, 'profiles');
      let constraints = [];
      
      // Add filters based on our profile structure
      if (university && university !== 'All Universities') {
        constraints.push(where('academic.university', '==', university));
      }
      
      if (course && course !== 'All Courses') {
        constraints.push(where('academic.degree', '==', course));
      }
      
      // We'll filter interests and personalities client-side since we can only use
      // one array-contains/array-contains-any per query in Firestore
      
      if (onlyOnline) {
        constraints.push(where('preferences.activityStatus', '==', 'Available'));
      }
      
      // Add ordering by lastUpdated
      constraints.push(orderBy('lastUpdated', 'desc'));
      
      // For search, we need to fetch more profiles initially
      const fetchLimit = normalizedSearchQuery ? Math.max(limitCount * 3, 60) : limitCount;
      constraints.push(limit(fetchLimit));
      
      // Add startAfter for pagination
      if (isLoadMore && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      // Create and execute the query
      const q = query(profilesQuery, ...constraints);
      const querySnapshot = await getDocs(q);
      
      // Check if there are more results
      setHasMore(!querySnapshot.empty);
      
      // Set last document for pagination
      if (!querySnapshot.empty) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }
      
      // Process results
      const fetchedProfiles = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const profileData = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };
        
        // Client-side filtering for interests
        let includeProfile = true;
        
        if (interests && interests.length > 0) {
          const profileInterests = profileData.bio?.interests || [];
          const hasMatchingInterest = interests.some(interest => 
            profileInterests.includes(interest)
          );
          
          if (!hasMatchingInterest) {
            includeProfile = false;
          }
        }
        
        // Client-side filtering for personalities
        if (personalities && personalities.length > 0) {
          const profilePersonalities = profileData.bio?.personalities || [];
          const hasMatchingPersonality = personalities.some(personality => 
            profilePersonalities.includes(personality)
          );
          
          if (!hasMatchingPersonality) {
            includeProfile = false;
          }
        }
        
        if (includeProfile) {
          fetchedProfiles.push(profileData);
        }
      }
      
      // Update all profiles state (for search)
      if (isLoadMore) {
        setAllProfiles(prevProfiles => [...prevProfiles, ...fetchedProfiles]);
      } else {
        setAllProfiles(fetchedProfiles);
      }
      
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Apply search to the fetched profiles
  const searchedProfiles = useMemo(() => {
    return searchProfiles(allProfiles, normalizedSearchQuery);
  }, [allProfiles, normalizedSearchQuery]);

  // Update profiles when search results change
  useEffect(() => {
    setProfiles(searchedProfiles);
  }, [searchedProfiles]);

  // Load more profiles for pagination
  const loadMore = () => {
    if (!loading && hasMore && !normalizedSearchQuery) {
      // Only allow pagination when not searching
      fetchProfiles(true);
    }
  };

  // Get a single profile by ID
  const getProfileById = async (profileId) => {
    try {
      setLoading(true);
      const profileRef = doc(db, 'profiles', profileId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        return { id: profileSnap.id, ...profileSnap.data() };
      } else {
        return null;
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Reset search results
  const clearSearch = () => {
    setProfiles(allProfiles);
  };

  // Get search statistics
  const getSearchStats = () => {
    return {
      totalProfiles: allProfiles.length,
      searchResults: searchedProfiles.length,
      isSearching: normalizedSearchQuery.length > 0
    };
  };

  // Initial fetch on mount and when dependencies change
  useEffect(() => {
    // Reset pagination when filters change
    setLastDoc(null);
    setHasMore(true);
    setAllProfiles([]);
    fetchProfiles();
  }, [university, course, JSON.stringify(interests), JSON.stringify(personalities), limitCount, onlyOnline]);

  // Determine if pagination should be available
  const canLoadMore = hasMore && !normalizedSearchQuery && !loading;

  return {
    profiles: searchedProfiles,
    loading,
    error,
    hasMore: canLoadMore,
    loadMore,
    getProfileById,
    refreshProfiles: () => {
      setLastDoc(null);
      setHasMore(true);
      setAllProfiles([]);
      fetchProfiles();
    },
    clearSearch,
    searchStats: getSearchStats()
  };
};

export default useProfiles;